export type HttpRequestParams = {
  [key: string]: any;
};
export default class RequestClient {
  apiUrl: string;

  constructor(url: string) {
    this.apiUrl = url;
  }

  async get<TRequest extends HttpRequestParams, TResponse>(
    path: string,
    params?: TRequest
  ) {
    let endpoint = normalizeUrl(this.apiUrl, path);
    if (params) {
      endpoint += buildQueryString(params);
    }
    const response = await fetch(endpoint, {
      headers: buildAuthHeader(),
    });
    return parseResponse<TResponse>(response);
  }

  async post<TRequest extends HttpRequestParams, TResponse>(
    path: string,
    params?: TRequest
  ) {
    let endpoint = normalizeUrl(this.apiUrl, path);
    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        ...buildAuthHeader(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(params),
    });
    return parseResponse<TResponse>(response);
  }

  async put<TRequest extends HttpRequestParams, TResponse>(
    path: string,
    params?: TRequest
  ) {
    let endpoint = normalizeUrl(this.apiUrl, path);
    const response = await fetch(endpoint, {
      method: "PUT",
      headers: {
        ...buildAuthHeader(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(params),
    });
    return parseResponse<TResponse>(response);
  }

  async delete<TRequest extends HttpRequestParams, TResponse>(
    path: string,
    params?: TRequest
  ) {
    let endpoint = normalizeUrl(this.apiUrl, path);
    if (params) {
      endpoint += buildQueryString(params);
    }
    const response = await fetch(endpoint, {
      method: "DELETE",
      headers: buildAuthHeader(),
    });
    return parseResponse<TResponse>(response);
  }

  async patch<TRequest extends HttpRequestParams, TResponse>(
    path: string,
    params?: TRequest
  ) {
    let endpoint = normalizeUrl(this.apiUrl, path);
    const response = await fetch(endpoint, {
      method: "PATCH",
      headers: {
        ...buildAuthHeader(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(params),
    });
    return parseResponse<TResponse>(response);
  }
}

const buildAuthHeader = (token?: string) => {
  if (token) {
    return { Authorization: `Bearer ${token}` };
  }
};

const buildQueryString = (params: HttpRequestParams) => {
  let queryParams = Object.keys(params).map((key) => `${key}=${params[key]}`);
  return `?${queryParams.join("&")}`;
};

const normalizeUrl = (url: string, path: string) => {
  if (url[url.length - 1] === "/") {
    url = url.slice(0, -1);
  }
  if (path[0] === "/") {
    path = path.substring(1);
  }

  return `${url}/${path}`;
};

type ParsedResponse<T> =
  | {
      ok: true;
      statusCode: number;
      body: T;
    }
  | {
      ok: false;
      statusCode: number;
      body: null;
    };
async function parseResponse<T>(
  response: Response
): Promise<ParsedResponse<T>> {
  let body: T | null = null;
  if (response.ok) {
    body = await response.json().catch(() => null);
    return {
      ok: true,
      statusCode: response.status,
      body: body as any,
    };
  } else {
    return {
      ok: false,
      statusCode: response.status,
      body,
    };
  }
}
